import { BaseItem, InternalData, StickyGroupBand } from '../../VirtualizedTable.types';

interface StickyGroupIndex<T extends BaseItem> {
  groupsByDepth: InternalData<T>[][];
  boundaryById: Map<string, string>;
  positionById: Map<string, number>;
}

export const buildRowsSignature = <T extends BaseItem>(data: InternalData<T>[]): string =>
  data.map((row) => `${row.id}:${row.depth}:${row.isParent ? 1 : 0}`).join('|');

export const areBandsEqual = <T extends BaseItem>(
  a: StickyGroupBand<T>[],
  b: StickyGroupBand<T>[],
): boolean =>
  a.length === b.length &&
  a.every((band, position) => band.row === b[position].row && band.push === b[position].push);

export const indexStickyGroups = <T extends BaseItem>(
  data: InternalData<T>[],
): StickyGroupIndex<T> => {
  const groupsByDepth: InternalData<T>[][] = [];
  const boundaryById = new Map<string, string>();
  const positionById = new Map<string, number>();
  const openGroups: InternalData<T>[] = [];

  data.forEach((item, position) => {
    positionById.set(item.id, position);

    let openGroup = openGroups.at(-1);
    while (openGroup && openGroup.depth >= item.depth) {
      boundaryById.set(openGroup.id, item.id);
      openGroups.pop();
      openGroup = openGroups.at(-1);
    }

    if (!item.isParent) {
      return;
    }

    groupsByDepth[item.depth] = groupsByDepth[item.depth] ?? [];
    groupsByDepth[item.depth].push(item);
    openGroups.push(item);
  });

  openGroups.forEach((group) => boundaryById.set(group.id, ''));

  return { groupsByDepth, boundaryById, positionById };
};

interface ResolveStickyBandsParams<T extends BaseItem> {
  index: StickyGroupIndex<T>;
  rowTops: Map<string, number>;
  firstRenderedPosition: number;
  chromeTop: number;
  bandHeight: number;
}

interface FindStuckGroupParams<T extends BaseItem> {
  groups: InternalData<T>[];
  rowTops: Map<string, number>;
  positionById: Map<string, number>;
  firstRenderedPosition: number;
  slotTop: number;
}

const findStuckGroup = <T extends BaseItem>({
  groups,
  rowTops,
  positionById,
  firstRenderedPosition,
  slotTop,
}: FindStuckGroupParams<T>): InternalData<T> | undefined => {
  const isStuck = (group: InternalData<T>) => {
    const top = rowTops.get(group.id);

    if (top === undefined) {
      return (positionById.get(group.id) ?? 0) < firstRenderedPosition;
    }

    return top < slotTop;
  };

  return groups.filter(isStuck).at(-1);
};

interface MeasureBandPushParams {
  boundaryTop?: number;
  slotTop: number;
  bandHeight: number;
}

const measureBandPush = ({ boundaryTop, slotTop, bandHeight }: MeasureBandPushParams): number => {
  if (boundaryTop === undefined) {
    return 0;
  }

  return Math.max(Math.min(boundaryTop - bandHeight - slotTop, 0), -bandHeight);
};

interface ResolveBandAtDepthParams<T extends BaseItem> {
  index: StickyGroupIndex<T>;
  rowTops: Map<string, number>;
  firstRenderedPosition: number;
  slotTop: number;
  bandHeight: number;
  depth: number;
  parent?: InternalData<T>;
}

const resolveBandAtDepth = <T extends BaseItem>({
  index,
  rowTops,
  firstRenderedPosition,
  slotTop,
  bandHeight,
  depth,
  parent,
}: ResolveBandAtDepthParams<T>): StickyGroupBand<T> | undefined => {
  const stuck = findStuckGroup<T>({
    groups: index.groupsByDepth[depth] ?? [],
    rowTops,
    positionById: index.positionById,
    firstRenderedPosition,
    slotTop,
  });

  if (!stuck || (parent && stuck.parentId !== parent.id)) {
    return undefined;
  }

  const push = measureBandPush({
    boundaryTop: rowTops.get(index.boundaryById.get(stuck.id) ?? ''),
    slotTop,
    bandHeight,
  });

  if (push <= -bandHeight) {
    return undefined;
  }

  return { row: stuck, position: index.positionById.get(stuck.id) ?? 0, push };
};

export const resolveStickyBands = <T extends BaseItem>({
  index,
  rowTops,
  firstRenderedPosition,
  chromeTop,
  bandHeight,
}: ResolveStickyBandsParams<T>): StickyGroupBand<T>[] => {
  const bands: StickyGroupBand<T>[] = [];

  index.groupsByDepth.forEach((_groups, depth) => {
    if (bands.length < depth) {
      return;
    }

    const band = resolveBandAtDepth<T>({
      index,
      rowTops,
      firstRenderedPosition,
      slotTop: chromeTop + depth * bandHeight,
      bandHeight,
      depth,
      parent: bands.at(-1)?.row,
    });

    if (band) {
      bands.push(band);
    }
  });

  return bands;
};
